/**
 * Snapshot gallery — localStorage-backed evidence archive per-tenant.
 * Stores small JPEG thumbnails (max ~150KB each) so a browser can keep 50+ items
 * without hitting the ~5MB localStorage quota. Full-resolution PNG downloads
 * still happen through the snapshot flow — this is a searchable catalogue.
 */
const MAX_ITEMS = 60;

function keyFor(tenantId) {
  return `cctv_snapshots_${tenantId || "default"}`;
}

export function loadSnapshots(tenantId) {
  try {
    const raw = localStorage.getItem(keyFor(tenantId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function saveSnapshot(tenantId, entry) {
  const list = loadSnapshots(tenantId);
  list.unshift(entry);
  // Cap items + drop oldest if quota errors out.
  const trimmed = list.slice(0, MAX_ITEMS);
  const key = keyFor(tenantId);
  let attempt = trimmed;
  while (attempt.length > 0) {
    try {
      localStorage.setItem(key, JSON.stringify(attempt));
      return attempt.length;
    } catch (_) {
      attempt = attempt.slice(0, Math.floor(attempt.length * 0.75));
    }
  }
  return 0;
}

export function deleteSnapshot(tenantId, id) {
  const list = loadSnapshots(tenantId).filter((s) => s.id !== id);
  localStorage.setItem(keyFor(tenantId), JSON.stringify(list));
  return list;
}

export function clearAllSnapshots(tenantId) {
  localStorage.removeItem(keyFor(tenantId));
}

// Build a downscaled JPEG data URL from a source <video> element.
// Returns { thumbUrl (small JPEG), fullBlob (Blob for immediate download PNG) }.
export function captureFromVideo(videoEl, overlayLines = []) {
  if (!videoEl || !videoEl.videoWidth) return null;
  const srcW = videoEl.videoWidth;
  const srcH = videoEl.videoHeight;

  // Full-resolution capture (returned as Blob for the direct-download flow).
  const full = document.createElement("canvas");
  full.width = srcW;
  full.height = srcH;
  const fctx = full.getContext("2d");
  fctx.drawImage(videoEl, 0, 0);
  overlayLines.forEach((line, i) => {
    // Overlay chrome — sized ~20% larger than the original 16/13px labels
    // for better legibility on downloaded evidence frames.
    const rowH = 27;      // was 22
    const rowW = 456;     // was 380 (~20% wider so bigger text still fits)
    const bigFont = "bold 19px";  // was 16px
    const smFont = "16px";        // was 13px
    fctx.fillStyle = "rgba(0,0,0,0.65)";
    fctx.fillRect(10, srcH - (rowH * 2 + 12) + i * rowH, rowW, rowH);
    fctx.fillStyle = i === 0 ? "#22d3ee" : "#e4e4e7";
    fctx.font = `${i === 0 ? bigFont : smFont} monospace`;
    fctx.fillText(line, 20, srcH - (rowH * 2 + 12) + i * rowH + rowH - 7);
  });

  // Thumbnail — max 480px wide, JPEG q=0.72.
  const thumbW = Math.min(480, srcW);
  const thumbH = Math.round((srcH / srcW) * thumbW);
  const thumb = document.createElement("canvas");
  thumb.width = thumbW;
  thumb.height = thumbH;
  thumb.getContext("2d").drawImage(full, 0, 0, thumbW, thumbH);
  const thumbUrl = thumb.toDataURL("image/jpeg", 0.72);

  return { fullCanvas: full, thumbUrl, srcW, srcH };
}
